import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useMobileMenu } from '../../App';

const Header: React.FC = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, currentUser, userCredits, userProfile } = useAuth();
  const { isMobileMenuOpen, toggleMobileMenu } = useMobileMenu();

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Get page title based on current route
  const getPageTitle = () => {
    switch (location.pathname) {
      case '/':
        return ''; // No title for dashboard
      case '/try-on':
        return 'Clothes AI Photoshoot';
      case '/ai-poses':
        return 'AI Pose Change';
      case '/ai-background-change':
        return 'AI Background Change';
      case '/ecommerce-photoshoot':
        return 'Ecommerce Photoshoot';
      case '/gym-wear-photoshoot':
        return 'Gym Wear Photoshoot';
      case '/yoga-wear-photoshoot':
        return 'Yoga Wear Photoshoot';
      case '/photoshoot':
        return 'Product AI Photoshoot';
      case '/fashion-extractor':
        return 'Outfit Extractor (Flatlay)';
      case '/ghost-mannequin':
        return 'Ghost Mannequin';
      case '/wrinkle-removal':
      case '/ai-wrinkle-removal':
        return 'Wrinkle Removal';
      case '/canvas-editor':
        return 'Canvas Editor';
      case '/credit-history':
        return 'Credit History';
      case '/profile':
        return 'Profile';
      case '/account-settings':
        return 'Account Settings';
      default:
        return 'AI Workflow';
    }
  };

  // Use profile data from backend, fallback to Firebase Auth data
  const userEmail = currentUser?.email || '';
  const fullName = (userProfile?.name || currentUser?.displayName || '').trim();
  const firstName = fullName ? fullName.split(/\s+/)[0] : (userEmail ? userEmail.split('@')[0] : 'Guest');
  const photoURLRaw = currentUser?.photoURL || '';
  const photoURL = photoURLRaw.trim();
  const hasPhoto = photoURL.length > 0;
  const [avatarError, setAvatarError] = useState(false);
  const isAuthenticated = !!currentUser;

  return (
    <header className="bg-white shadow-sm py-2 md:py-2.5 px-3 md:px-6 flex items-center justify-between">
      <div className="flex items-center space-x-4">
        {/* Mobile menu toggle */}
        <button
          className="md:hidden text-text-description hover:text-text-heading"
          onClick={toggleMobileMenu}
        >
          <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'} text-xl`}></i>
        </button>


        {isAuthenticated && (
          <div className="hidden md:flex items-center space-x-2">
            <div className="flex items-center space-x-2 bg-secondary-100 rounded-full py-1.5 px-4 text-sm border border-secondary-200 shadow-sm">
              <span className="text-secondary-600 font-semibold tracking-tight">Logged In</span>
              <div className="w-px h-3 bg-secondary-300 mx-1"></div>
              <i className="fas fa-coins text-gold text-xs"></i>
              <span className="text-secondary-700 font-bold">{userCredits.toFixed(2)} Credits</span>
              <span className="ml-1 w-2 h-2 bg-success-500 rounded-full animate-pulse"></span>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center space-x-2 md:space-x-4">
        <div className="w-px h-8 bg-accent-taupe hidden md:block"></div>
        <div className="relative" ref={dropdownRef}>
          <button
            className="flex items-center space-x-2 hover:bg-accent-sand rounded-full px-2 py-1"
            onClick={toggleDropdown}
          >
            {hasPhoto && !avatarError ? (
              <img
                src={photoURL}
                alt="User avatar"
                className="w-8 h-8 rounded-full object-cover"
                onError={() => setAvatarError(true)}
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-accent-taupe flex items-center justify-center overflow-hidden">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="w-4 h-4 text-text-description"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path fillRule="evenodd" d="M18 8a6 6 0 11-12 0 6 6 0 0112 0zm-6 8c-5.523 0-10 2.239-10 5v1h20v-1c0-2.761-4.477-5-10-5z" clipRule="evenodd" />
                </svg>
              </div>
            )}
            <span className="text-sm font-medium hidden md:inline">{firstName}</span>
            <i className={`fas fa-chevron-${dropdownOpen ? 'up' : 'down'} text-xs text-text-description hidden md:inline-block`}></i>
          </button>

          {/* Dropdown menu */}
          {dropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-accent-taupe">
              {isAuthenticated && (
                <div className="px-4 py-2 border-b border-accent-taupe">
                  <p className="font-medium text-sm">{userProfile?.name || fullName || firstName}</p>
                  <p className="text-xs text-text-description truncate">{userEmail}</p>
                </div>
              )}
              <button
                onClick={() => {
                  navigate('/profile');
                  setDropdownOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-text-heading hover:bg-accent-sand"
              >
                Profile
              </button>
              <button
                onClick={() => {
                  navigate('/account-settings');
                  setDropdownOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-text-heading hover:bg-accent-sand"
              >
                Account Settings
              </button>
              <div className="border-t border-accent-taupe my-1"></div>
              <button
                onClick={() => {
                  handleLogout();
                  setDropdownOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-secondary-600 hover:bg-accent-sand"
              >
                <i className="fas fa-sign-out-alt mr-2"></i>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header; 